import './UserOwner.css';

import React, { useEffect, useState } from 'react';
import { useLoginContext } from '../../App';
import { generateClassName, isUserHavingAnyOfTheseRoles } from './helpers';
import WaitingSpinner from './WaitingSpinner';
import { fetchUserByUserUUID, fetchUsersHavingRoles } from '../../services/b2b-users-apiService';
import { showNotification } from './Notification';
import { useContentContext } from '../layout/Layout';
import { FormControl, Input, InputAdornment, InputLabel, MenuItem, Select } from '@mui/material';
import { AccountCircle, Edit, HighlightOff, Save } from '@mui/icons-material';
import useB2BTranslation from './hooks/useB2BTranslation';

const Roles = ({ roles }) => {
    const { t } = useB2BTranslation('b2b-common');
    // console.log('roles', roles);
    return (
        <div className='roles-container'>
            {roles.map((role, index) => (
                <div key={index} className='role-name'>
                    {t(role.roleName)}
                </div>
            ))}
        </div>
    );
}

const SelectOtherUserMember = ({ currentMember, members, inputLabel = null, className = null, onSelect, onCancel }) => {
    const { t } = useB2BTranslation('b2b-common');
    const [selectedMember, setSelectedMember] = useState(currentMember);

    const handleSelect = (event) => {
        const selectedUser = members.find(member => member.user.userUuid === event.target.value);
        setSelectedMember(selectedUser);
    };

    const handleSave = () => {
        onSelect && onSelect(selectedMember);
    };

    const handleCancel = () => {
        onCancel && onCancel();
    }

    return (
        <FormControl variant='standard' className={generateClassName('select-other-user-member', '-form-control', className)}>
            <InputLabel htmlFor='select-other-user-member' className='select-other-user-member-input-label'>
                {inputLabel || t('Select Member')}
            </InputLabel>
            <Select
                id='select-other-user-member'
                value={selectedMember.user.userUuid}
                onChange={handleSelect}
                startAdornment={
                    <InputAdornment position='start' className='select-other-user-member-start-adornment'>
                        <AccountCircle />
                    </InputAdornment>
                }
                endAdornment={
                    <InputAdornment position='end' className='select-other-user-member-end-adornment'>
                        <Roles roles={selectedMember.roles} />
                        <Save className='select-other-user-member-icon' onClick={handleSave} />
                        <HighlightOff className='select-other-user-member-icon' onClick={handleCancel} />
                    </InputAdornment>
                }
            >
                {members.map((member) => (
                    <MenuItem key={member.user.userUuid} value={member.user.userUuid}>
                        {`${member.user.firstName} ${member.user.lastName}`}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

const UserMember = ({ member, inputLabel = null, className = null, isEditable = true, onEdit }) => {
    const { t } = useB2BTranslation('b2b-common');

    const handleEdit = () => {
        onEdit && onEdit(member);
    }

    return (
        <FormControl variant='standard' className={generateClassName('user-member', '-form-control', className)}>
            <InputLabel htmlFor='user-member' className='user-member-input-label' >{inputLabel || t('Member')}</InputLabel>
            <Input
                id='user-member'
                startAdornment={
                    <InputAdornment position='start' className='user-member-start-adornment'>
                        <AccountCircle />
                    </InputAdornment>
                }
                value={`${member.user.firstName} ${member.user.lastName}`}
                endAdornment={
                    <InputAdornment position='end' className='user-member-end-adornment'>
                        <Roles roles={member.roles} />
                        {isEditable &&
                            <Edit className='user-member-edit-icon' onClick={handleEdit} />
                        }
                    </InputAdornment>
                }
                readOnly
            />
        </FormControl>
    );
}

const UserOwner = ({ userUUID, className = null, isEditable = true, onOwnerChange }) => {
    const { loginData } = useLoginContext();
    const { t } = useB2BTranslation('b2b-common');
    const { setNotification } = useContentContext();

    const [currentOwner, setCurrentOwner] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [prevUserUUID, setPrevUserUUID] = useState(null);

    const [showSelectOtherUserMember, setShowSelectOtherUserMember] = useState(false);
    const [eligibleOrganizationOwners, setEligibleOrganizationOwners] = useState([]);

    const findUser = async (userUUID) => {
        try {
            setIsLoading(true);
            const user = await fetchUserByUserUUID(loginData.accessToken, loginData.loggedOrganizationUuid, userUUID);
            setCurrentOwner(user);
            return user;
        } catch (error) {
            console.error('Error fetching user:', error);
            showNotification(setNotification, t('Error fetching user ({{userUUID}}): {{error}}', { userUUID: userUUID, error: error.response.statusText }), 'error', 5000);
        } finally {
            setIsLoading(false);
        }
    };

    const getEligibleOrganizationUsers = async () => {
        try {
            const allPotentialUsers = await fetchUsersHavingRoles(loginData.accessToken, loginData.loggedOrganizationUuid, ['ADMIN', 'EDITOR']);
            setEligibleOrganizationOwners(allPotentialUsers);
            return allPotentialUsers;
        } catch (error) {
            console.error('Error fetching eligible users', error);
            showNotification(setNotification, t('Error fetching eligible users: ') + error.response.statusText, 'error', 5000);
        }
    }

    useEffect(() => {
        const checkUserChange = async () => {
            if (userUUID && userUUID !== prevUserUUID) {
                findUser(userUUID);
                setPrevUserUUID(userUUID);
            }
        };
        checkUserChange();
    }, [userUUID, prevUserUUID]);

    if (isLoading) {
        return <WaitingSpinner text={t('Loading ownership info...')} size={15} className={'user-owner-spinner'} />;
    }

    const handleRequestToEditOwner = async () => {
        // console.log('UserOwner handleRequestToEditOwner:', currentOwner);
        const eligibleUsers = await getEligibleOrganizationUsers();
        // console.log('UserOwner handleRequestToEditOwner eligibleUsers:', eligibleUsers);
        if (eligibleUsers && eligibleUsers.length > 0) {
            setShowSelectOtherUserMember(true);
        }
    }

    const handleRequestToChangeOwner = (newOwner) => {
        onOwnerChange && onOwnerChange(newOwner);
        setShowSelectOtherUserMember(false);
    }

    return (
        !isLoading &&
        currentOwner && (
            <div className={generateClassName('user-owner', '', className)}>
                {showSelectOtherUserMember &&
                    <SelectOtherUserMember
                        currentMember={currentOwner}
                        members={eligibleOrganizationOwners}
                        inputLabel={t('Select New Owner')}
                        onSelect={handleRequestToChangeOwner}
                        onCancel={() => setShowSelectOtherUserMember(false)}
                    />
                }
                {!showSelectOtherUserMember &&
                    <UserMember member={currentOwner} inputLabel={t('Owner')}
                        isEditable={isEditable && isUserHavingAnyOfTheseRoles(loginData, ['ADMIN'])}
                        onEdit={handleRequestToEditOwner}
                    />
                }
            </div>
        )
    );
}

export default UserOwner;
